// File Name: pages/match-pages/matchClock.js
//
// The live match clock, ported from the app's LiveScoreboardScreen._timerText.
// Pure functions, no React — so it can be unit tested, which matters because
// three parts of it are easy to get subtly wrong:
//
//   * `pausedAt` FREEZES the clock. A watcher must never see time run down
//     while play is stopped.
//   * Overtime reads "+MM:SS", not a negative number.
//   * Half time depends on `timingMode`, and can be implied by an expired
//     first-half clock rather than the explicit `halfTime` flag.
//
// The clock is advisory and derived from the viewer's device time, exactly as
// in the app. A skewed device clock shows a skewed timer; matching the app
// matters more than being right.

import { toMillis } from '../../lib/matchTime';

export function mmss(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Frozen at the pause, exactly like the host's screen. */
export function clockNow(match, nowMs) {
  return toMillis(match.pausedAt) ?? nowMs;
}

export function clockExpired(match, nowMs) {
  const ends = toMillis(match.endsAt);
  return ends != null && clockNow(match, nowMs) > ends;
}

export function atHalfTime(match, nowMs) {
  return (
    match.timingMode === 'halves' &&
    (match.halfTime === true ||
      ((match.currentHalf ?? 1) === 1 && clockExpired(match, nowMs)))
  );
}

export function isPaused(match) {
  return toMillis(match.pausedAt) != null;
}

export function isOver(match) {
  return match.status === 'ended' || match.status === 'abandoned';
}

/**
 * The timer string. Empty means "render no timer at all".
 *
 * @param match   the match document
 * @param nowMs   Date.now(), passed in so this stays pure and testable
 * @param halfTimeLabel  translated "Half Time", uppercased by the caller's copy
 */
export function timerText(match, nowMs, halfTimeLabel) {
  if (!match || match.status !== 'live') return '';
  if (atHalfTime(match, nowMs)) return String(halfTimeLabel).toUpperCase();

  const now = clockNow(match, nowMs);
  const started = toMillis(match.startedAt);
  if (started == null) return '';

  // Counts UP, like a referee's watch. This used to count DOWN whenever a
  // duration was set; the app switched to a stopwatch on 2026-09-07 and the two
  // must agree, because the same match is on both screens at once.
  //
  // `startedAt` is the only input because the app pushes it forward past every
  // pause, half-time interval and pre-extra-time gap — so the difference from
  // it is playing time and nothing else.
  const elapsed = (now - started) / 1000;

  const ends = toMillis(match.endsAt);
  if (ends == null) return mmss(elapsed); // untimed: a plain stopwatch

  // The scheduled end, expressed as an elapsed value, so it follows the host's
  // +5/+10 extends, the second half and extra time with no extra bookkeeping.
  const cap = (ends - started) / 1000;
  if (cap <= 0 || elapsed <= cap) return mmss(elapsed);
  // Football's added-time form, e.g. "45:00 +2:30".
  return `${mmss(cap)} +${mmss(elapsed - cap)}`;
}

/**
 * Extra time, in football's shorthand — "90+10" — or '' when none was played.
 *
 * Mirrors `MatchModel.extraTimeLabel`. Regulation is `durationMin` doubled for
 * two halves and taken as-is otherwise, which is the whole reason this can't
 * just print `durationMin`.
 */
export function extraTimeLabel(match) {
  const extra = match?.extraTimeMin ?? 0;
  if (!extra) return '';
  const mode = match.timingMode;
  if (!mode || mode === 'none') return '';
  const duration = match.durationMin ?? 0;
  const regulation = mode === 'halves' ? duration * 2 : duration;
  if (!regulation) return '';
  return `${regulation}+${extra}`;
}

/**
 * Status pill contents: which dot colour and which copy key.
 * Mirrors the app's ordering — ended beats paused beats live.
 */
export function statusFor(match) {
  if (!match) return { tone: 'lobby', key: 'lobby' };
  if (isOver(match)) return { tone: 'over', key: 'fullTime' };
  if (isPaused(match)) return { tone: 'paused', key: 'paused' };
  if (match.status === 'live') return { tone: 'live', key: 'live' };
  return { tone: 'lobby', key: 'lobby' };
}
